<script>
import axios from 'axios';
import sortArrayBySubObjects from '@/helpers/sortArrayBySubObjects';

export default {
    props: {
        fieldType: {
            type: String,
            required: true
        },

        fieldId: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            populations: [],
            professions: [],
            loading: true
        };
    },

    watch: {
        fieldId() {
            this.setupPanels();

        }
    },

    mounted() {
        if (this.fieldId) {
            this.setupPanels();
        }
    },

    methods: {
        setupPanels() {
            this.getPopulations()
                .then(() => {
                    this.getProfessions()
                        .then(() => {
                            this.loading = false;
                        });
                });
        },

        getPopulations() {
            return new Promise(resolve => {
                axios.get('/api/field/populations/' + this.fieldId)
                    .then(response => {
                        this.populations = sortArrayBySubObjects('profession.name', response.data.data.populations);
                        resolve();
                    });
            });
        },

        getProfessions() {
            return new Promise(resolve => {
                axios.get('/api/specialty/index')
                    .then(response => {
                        this.professions = response.data.professions;
                        resolve();
                    });
            });
        },

        groupFieldPopulation(group) {
            let population = [];
            const fieldGroup = group;
            const numIterations = fieldGroup[0].length;
            const numFieldsPerIteration = fieldGroup.length;

            for (let i = 0; i <= numIterations - 1; i++) {
                population[i] = [];
            }

            for (let i = 0; i <= numIterations - 1; i++) {
                for (let f = 0; f <= numFieldsPerIteration - 1; f++) {
                    population[i].push(fieldGroup[f][i]);
                }
            }

            return population;
        },

        professionTitle(id) {
            const profession = this.professions.find(o => o.id === id);
            if (profession) {
                return profession.name;
            }
        }
    }
};
</script>

<template>
    <div class="PopulationPanels">
        <transition name="fade">
            <v-expansion-panels
                v-if="!loading"
                class="PopulationPanels__panels"
            >
                <v-expansion-panel
                    v-for="(pop, index) in populations"
                    :key="index+pop.id"
                >
                    <v-expansion-panel-header>
                        {{ pop.profession.name }}
                    </v-expansion-panel-header>

                    <v-expansion-panel-content>
                        <div v-if="fieldType === 'text'">
                            {{ pop.population.text }}
                        </div>

                        <div v-else-if="fieldType === 'textarea'">
                            {{ pop.population.paragraphText }}
                        </div>

                        <div v-else-if="fieldType === 'wysiwyg'">
                            <div v-html="pop.population.text" />
                        </div>

                        <div v-else-if="fieldType === 'group'">
                            <div class="PopulationPanels__populations">
                                <div
                                    class="PopulationPanels__population"
                                    v-for="(population, popIdx) in groupFieldPopulation(pop.population.data)"
                                    :key="popIdx"
                                >
                                    <h4>
                                        Group: {{ popIdx + 1 }}
                                    </h4>
                                    <div
                                        v-for="(popField, popFieldIdx) in population"
                                        :key="popFieldIdx"
                                        class="PopulationPanels__population__field"
                                    >
                                        {{ popField }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <v-card-actions>
                            <v-spacer />
                            <v-chip
                                small
                                :color="pop.published ? 'primary' : 'error'"
                                style="margin-right: 4px;"
                            >
                                <v-icon
                                    v-if="pop.published"
                                    x-small
                                    left
                                >
                                    fas fa-check-circle
                                </v-icon>
                                {{ pop.published ? 'Published' : 'Not Published' }}
                            </v-chip>
                            <v-btn
                                small
                                color="secondary"
                                :to="'/population/edit/' + pop.id"
                            >
                                <v-icon
                                    x-small
                                    left
                                >
                                    fas fa-edit
                                </v-icon>
                                Edit
                            </v-btn>
                        </v-card-actions>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
            <div
                class="PopulationPanels__loader"
                v-else
            >
                <v-progress-linear
                    indeterminate
                    color="primary"
                />
            </div>
        </transition>
    </div>
</template>

<style lang="scss">
.fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */
{
    opacity: 0;
}

.PopulationPanels {
    position: relative;
    flex: 0 0 100%;
    width: 100%;
    min-height: 300px;
    margin-top: 10px;
}

.PopulationPanels__panels {
    position: relative;
    top: 0;
    z-index: 1;
}

.PopulationPanels__loader {
    position: absolute;
    top: 0;
    z-index: 2;
    display: flex;
    flex-flow: row wrap;
    justify-content: flex-start;
    align-content: flex-start;
    align-items: flex-start;
    flex: 0 0 100%;
    width: 100%;
}

.PopulationPanels__subhead {
    flex: 0 0 100%;
    width: 100%;
    font-weight: 500;
}

.PopulationPanels__populations {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
    flex: 0 0 100%;
    width: 100%;
}

.PopulationPanels__population {
    display: flex;
    flex-flow: row wrap;
    flex: 0 0 100%;
    width: 100%;
    margin-bottom: 8px;

    h4 {
        flex: 0 0 100%;
        width: 100%;
    }
}

.PopulationPanels__population__field {
    display: flex;
    flex-flow: row wrap;
    flex: 0 0 100%;
    width: 100%;
}

.PopulationPanels__data__item {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
    flex: 0 0 100%;
    width: 100%;
    padding-bottom: 12px;
    border-bottom: 1px solid #cecece;
}

.PopulationPanels__data__item {
    flex: 0 0 100%;
    width: 100%;
    margin-bottom: 12px;

    h4 {
        flex: 0 0 100%;
        width: 100%;
        margin-bottom: 6px;
    }
}

.PopulationPanels__data__item__a {
    margin-bottom: 6px;
}

.PopulationPanels__data__item__a,
.PopulationPanels__data__item__b {
    flex: 0 0 100%;
    width: 100%
}

.jsDataItemInline {
    .PopulationPanels__data__item__a,
    .PopulationPanels__data__item__b {
        flex: 0 0 49%;
        width: 49%
    }
}
</style>
